import React, { useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import styled, { keyframes } from 'styled-components'
import BuffetContainer from 'components/buffet/BuffetContainer'
import LabelWithIcon from 'components/base-ui/LabelWithIcon'
import { useTranslation } from 'react-i18next'
import { Button, Fonts, ScrollPage, ContentResize } from 'components/base-ui'
import GlobalCartLineItem from 'components/dine-in/GlobalCartLineItem'
import GlobalCartLineItemTakeAway from 'components/dine-in/GlobalCartLineItemTakeAway'
import colors from 'styles/colors'
import {
  OrderStatusEnum,
  StaffCallingReasonEnum,
  useCallStaffMutation,
  LineItemServiceTypeForBuffetEnum,
} from 'graphQL/dinein/generate/operations'
import { dineInClient } from 'variables/services'
import { useSelector } from 'react-redux'
import { buffetSelector, setBuffetModal } from 'store/slices/buffetSlice'
import useGlobalCart from 'hooks/useGlobalCart'
import { useHistory } from 'react-router-dom'
import { SubPageName } from 'enums/buffet'
import { displayPrice2Digit } from 'helpers'
import TextWithTooltip from './TextWithTooltip'
import useLanguage from 'hooks/useLanguage'
import ModalFinishEating from './ModalFinishEating'
import useValidateCurrentOrder, { OrderInvalidEnum } from 'hooks/buffet/useValidateCurrentOrder'
import { useAppDispatch } from 'store'
import * as Sentry from '@sentry/browser'
import { isDevelopment } from 'variables/environment'
import useModal from 'hooks/useModal'
import { ModalEnum } from 'components/buffet/BuffetModals'
import EmptyLineItems from 'pages/dine-in/pages/EmptyLineItems'
import TopLabel from 'components/buffet/TopLabel'
import BtnCircle from 'components/base-ui/BtnCircle'
import Receipt from './Receipt'
import OverlayPage from 'components/dine-in/OverlayPage'
import SummaryOrder from 'components/SummaryOrder'
import useBuffetGlobalCart from 'hooks/buffet/useBuffetGlobalCart'
import ExVatInfo, { WrapperWithExVatInfo } from 'components/ExVatInfo'
import { vendorSelector } from 'store/slices/vendorSlice'

const { primary } = colors

const GlobalCart = () => {
  const { t } = useTranslation()
  const buffet = useSelector(buffetSelector)
  const dispatch = useAppDispatch()
  const history = useHistory()
  const { getPropertyByLang } = useLanguage()
  const modalFinishEating = useModal()
  const modalReadyToCheckout = useModal()
  const scrollPageRef = useRef<HTMLDivElement>(null)
  const callCurrentOrder = useRef<ReturnType<typeof setInterval>>()
  const [isShowBtnScrollDown, setIsShowBtnScrollDown] = useState(true)
  const [isCanScroll, setIsCanScroll] = useState(false)
  const {
    setting: { excludingVat },
  } = useSelector(vendorSelector)

  const {
    summaryGlobalCart,
    isHavePromoDiscount,
    globalCart,
    currentOrder,
    buffetItems,
    buffetPackages,
    lineItems,
    unSubscribeOrder,
    refreshBuffetOrder,
    refreshBuffetOrderAndPromotion,
  } = useBuffetGlobalCart()
  const isReadyToCheckout = buffet.currentOrder.status === OrderStatusEnum.ReadyToCheckout
  const { validateCurrentOrder } = useValidateCurrentOrder()

  useEffect(() => {
    return () => {
      unSubscribeOrder()
      callCurrentOrder.current && clearInterval(callCurrentOrder.current)
    }
  }, [])

  const intervalCallCurrentOrder = () => {
    callCurrentOrder.current && clearInterval(callCurrentOrder.current)
    callCurrentOrder.current = setInterval(async () => {
      try {
        const result = await refreshBuffetOrder()
        if (
          result.data?.currentOrder.staffCallingReason !== StaffCallingReasonEnum.Checkout &&
          callCurrentOrder.current
        ) {
          clearInterval(callCurrentOrder.current)
          await refreshBuffetOrderAndPromotion()
        }
      } catch (error) {
        !isDevelopment &&
          Sentry.captureException(new Error(`Error from interval call current order : ${JSON.stringify(error)}`))
      }
    }, 3000)
  }

  const [callStaff, { loading: callStaffLoading }] = useCallStaffMutation({
    client: dineInClient,
    variables: {
      orderID: buffet.table.orderID,
      reason: StaffCallingReasonEnum.Checkout,
    },
    onCompleted: () => {
      dispatch(setBuffetModal({ type: ModalEnum.CallingStaff, isProcessing: false }))
      modalFinishEating.reset()
      if (buffet.subscribeOrderFailed) {
        intervalCallCurrentOrder()
      }
    },
    onError: (error) => {
      modalFinishEating.reset()
      !isDevelopment && Sentry.captureException(new Error(`Buffet error from call staff : ${JSON.stringify(error)}`))
    },
  })

  const handleScrollPage = () => {
    const element = scrollPageRef.current
    const isScrollToBottom = element && element.scrollTop + element.clientHeight > element.scrollHeight - 25
    if (isScrollToBottom) {
      setIsShowBtnScrollDown(false)
    } else if (isCanScroll) {
      setIsShowBtnScrollDown(true)
    }
  }

  const handleClickFinishEating = async () => {
    if (isReadyToCheckout) {
      modalReadyToCheckout.setOpen(true)
      return
    }

    modalFinishEating.setOpen(true)
  }

  const handleClickCheckout = () => {
    const validateResult = validateCurrentOrder(buffet.currentOrder, false)
    if (validateResult.isOK || validateResult.invalid === OrderInvalidEnum.ReadyToCheckout) {
      dispatch(setBuffetModal({ type: ModalEnum.ConfirmCheckout, isProcessing: false }))
    } else if (validateResult.invalid === OrderInvalidEnum.AnotherHasOccupied) {
      dispatch(setBuffetModal({ type: ModalEnum.ConfirmOverrideCheckout, isProcessing: false }))
    } else if (validateResult.invalid === OrderInvalidEnum.PendingPayment) {
      dispatch(setBuffetModal({ type: ModalEnum.PendingPayment, isProcessing: false }))
    }
  }

  const handleScrollToBotom = () => {
    scrollPageRef.current?.scrollTo({
      left: 0,
      top: scrollPageRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }

  const handleContentResize = _.debounce((contentWrapper: HTMLDivElement | null) => {
    const topLabel = scrollPageRef.current?.querySelector<HTMLDivElement>('#top-label')
    if (contentWrapper && topLabel) {
      const _isCanScroll = window.innerHeight < contentWrapper.offsetHeight + topLabel.offsetHeight
      setIsCanScroll(_isCanScroll)
      setIsShowBtnScrollDown(_isCanScroll)
    }
  }, 200)

  const handleTopLabelResize = _.debounce((topLabel: HTMLDivElement | null) => {
    const contentWrapper = scrollPageRef.current?.querySelector<HTMLDivElement>('#product-content-wrapper')
    if (topLabel && contentWrapper) {
      const _isCanScroll = window.innerHeight < contentWrapper.offsetHeight + topLabel.offsetHeight
      setIsCanScroll(_isCanScroll)
      setIsShowBtnScrollDown(_isCanScroll)
    }
  }, 200)

  const { globalCarts } = useGlobalCart({ globalCart })

  return (
    <BuffetContainer activeFooter={!isReadyToCheckout} currentMenu="Submitted Order">
      <ScrollPage id="global-cart-page" ref={scrollPageRef} onScroll={handleScrollPage}>
        {!isReadyToCheckout && <TopLabel endTime={buffet.endTime} resizeCallback={handleTopLabelResize} />}
        <Wrapper>
          {currentOrder && (
            <>
              <ContentResize
                id="product-content-wrapper"
                style={{ padding: '15px' }}
                resizeCallback={handleContentResize}
              >
                {globalCarts.length > 0 ? (
                  <>
                    <TitleWrapper>
                      <LabelWithIcon
                        title={t('buffet.globalCartPage.header')}
                        size="lg"
                        bold
                        iconName="board-check"
                        iconSize="20px"
                      />
                    </TitleWrapper>
                    <OrderListSection>
                      {globalCarts.map((globalCart, index) => {
                        switch (globalCart.serviceType) {
                          case LineItemServiceTypeForBuffetEnum.BuffetALaCarteTakeaway:
                            return (
                              <GlobalCartLineItemTakeAway
                                key={index}
                                globalCart={globalCart}
                                buffetPackageName={`${t('buffet.globalCartPage.package')} ${getPropertyByLang({
                                  en: currentOrder.buffetPackage.nameEn,
                                  th: currentOrder.buffetPackage.nameTh,
                                })}`}
                              />
                            )
                          default:
                            return (
                              <GlobalCartLineItem
                                key={index}
                                globalCart={globalCart}
                                buffetPackageName={`${t('buffet.globalCartPage.package')} ${getPropertyByLang({
                                  en: currentOrder.buffetPackage.nameEn,
                                  th: currentOrder.buffetPackage.nameTh,
                                })}`}
                              />
                            )
                        }
                      })}
                    </OrderListSection>
                    {!isReadyToCheckout &&
                      buffet.currentOrder.staffCallingReason !== StaffCallingReasonEnum.Checkout &&
                      !buffet.isTimeUp && (
                        <ButtonOrderWrapper>
                          <Button
                            testId="orderMoreFoodBtn"
                            id="more-order"
                            outline
                            onClick={() => history.push(SubPageName.Home)}
                            enableFastClick={false}
                          >
                            {t('buffet.globalCartPage.button.makeMoreOrder')}
                          </Button>
                        </ButtonOrderWrapper>
                      )}
                    <BuffetItemsWrapper>
                      {buffetPackages.map((item, index) => (
                        <BuffetItem key={index}>
                          <Fonts.BaseTitleRegular>{item.name}</Fonts.BaseTitleRegular>
                          <Fonts.BaseTitleRegular>{displayPrice2Digit(item.totalSatang / 100)}</Fonts.BaseTitleRegular>
                        </BuffetItem>
                      ))}
                    </BuffetItemsWrapper>
                    {buffetItems.length > 0 && (
                      <BuffetItemsWrapper>
                        {buffetItems.map((item, index) => (
                          <BuffetItem key={index}>
                            <Fonts.BaseTitleRegular>{item.name}</Fonts.BaseTitleRegular>
                            <Fonts.BaseTitleRegular>
                              {displayPrice2Digit(item.totalSatang / 100)}
                            </Fonts.BaseTitleRegular>
                          </BuffetItem>
                        ))}
                      </BuffetItemsWrapper>
                    )}
                    {isHavePromoDiscount && summaryGlobalCart && (
                      <SummaryOrder adjustments={summaryGlobalCart.adjustments} />
                    )}
                    <SummarySection id="summary-section">
                      <PriceWrapper>
                        <div>
                          <Fonts.Medium>{t('buffet.globalCartPage.total')}</Fonts.Medium>
                        </div>
                        <Fonts.Medium>{displayPrice2Digit(currentOrder.totalSatangs / 100)}</Fonts.Medium>
                      </PriceWrapper>
                    </SummarySection>
                    <StickyWrapper id="order-action" isSticky={isCanScroll}>
                      {!isReadyToCheckout && (
                        <WrapperWithExVatInfo>
                          <BtnFinishEating
                            id="checkout-finish-eating"
                            data-testid="finishedEatingBtn"
                            disabled={buffet.currentOrder.staffCallingReason === StaffCallingReasonEnum.Checkout}
                            onClick={handleClickFinishEating}
                          >
                            {t('buffet.globalCartPage.button.finishEating')}
                          </BtnFinishEating>
                          {excludingVat && <ExVatInfo />}
                        </WrapperWithExVatInfo>
                      )}

                      <TextWithTooltip
                        icon="information"
                        label={t('buffet.globalCartPage.checkoutTitle')}
                        tooltipTitle={t('buffet.globalCartPage.tooltip.title')}
                        tooltipDescription={t('buffet.globalCartPage.tooltip.description')}
                      />
                      {isCanScroll && isShowBtnScrollDown && (
                        <BtnCircleWrapper id="btn-circle-wrapper">
                          <BtnCircle onClick={handleScrollToBotom} icon="arrow-down" iconSize="16px" />
                        </BtnCircleWrapper>
                      )}
                    </StickyWrapper>
                  </>
                ) : (
                  <EmptyLineItems onClick={() => history.push(SubPageName.Home)} />
                )}
              </ContentResize>
            </>
          )}
          <ModalFinishEating
            isOpen={modalFinishEating.open}
            isLoading={callStaffLoading}
            onRequestClose={() => {
              if (callStaffLoading) return
              modalFinishEating.setOpen(false)
            }}
            onAccept={callStaff}
          />

          <OverlayPage animation isShow={isReadyToCheckout}>
            <Receipt
              buffetItems={buffetItems}
              lineItems={lineItems}
              buffetPackages={buffetPackages}
              total={currentOrder?.totalSatangs ?? 0}
              onCheckout={handleClickCheckout}
              adjustments={summaryGlobalCart?.adjustments ? summaryGlobalCart.adjustments : []}
            />
          </OverlayPage>
        </Wrapper>
      </ScrollPage>
    </BuffetContainer>
  )
}

export default GlobalCart

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const OrderListSection = styled.div`
  display: flex;
  flex-direction: column;
`

const ButtonOrderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 25px;
  padding-bottom: 25px;
  button {
    min-width: 220px;
  }

  position: relative;
  &::before,
  &::after {
    content: '';
    display: block;
    height: 1px;
    position: absolute;
    right: 0;
    left: 0;
    background-color: ${({ theme }) => theme.colors.baseGrey};
    opacity: 0.5;
  }
  &::before {
    top: 0;
  }
  &::after {
    bottom: 0;
  }
`

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  align-items: center;
  row-gap: 20px;
  padding-left: 18px;
  padding-right: 18px;
  padding-bottom: 10px;
  width: 100vw;
  background-color: ${({ theme }) => theme.colors.white};
`

const PriceWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  padding-top: 15px;

  position: relative;
  &::before {
    content: '';
    display: block;
    height: 1px;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    background-color: ${({ theme }) => theme.colors.baseGrey};
    opacity: 0.5;
  }
`

const BtnFinishEating = styled(Button)`
  max-width: 300px;
  min-height: 50px;
  border-radius: 85px;
  align-self: center;

  background-color: ${primary} !important;
  border: 1px solid ${primary};

  .no-touch &:hover,
  .touch &:active {
    background-color: ${({ theme }) => theme.colors.white}!important;
    color: ${primary};
  }

  :disabled {
    color: ${({ theme }) => theme.colors.white}!important;
    background: ${({ theme }) => theme.colors.baseGrey}!important;
    border: 1px solid ${({ theme }) => theme.colors.baseGrey};
  }
`

const BuffetItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  padding-top: 14px;
  padding-bottom: 14px;
`

const BuffetItem = styled.div`
  display: flex;
  justify-content: space-between;
`

const fadeInAnimation = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`

const BtnCircleWrapper = styled.div`
  position: absolute;
  top: -50px;
  right: 18px;
  z-index: 99;
  animation: ${fadeInAnimation} 0.5s ease-in-out;
  .button {
    background: ${({ theme }) => theme.colors.white};
    border: none;
    box-shadow: 0px 0px 10px #e0e0e0;
  }
`

const StickyWrapper = styled(SummarySection)<{ isSticky: boolean }>`
  position: sticky;
  bottom: 15px;
  padding-bottom: 60px;
  ${({ isSticky }) =>
    isSticky &&
    `
    padding-top: 20px;
    box-shadow: 0px 0px 10px #e0e0e0;
  `}
`

const TitleWrapper = styled.div`
  margin-bottom: 10px;
`
